import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { I18n } from 'aws-amplify/utils';
import { ALL_CODES } from "./utils/languages";
import personaV2Labels from "./utils/personaV2Labels";
import uiLabelsAugment from "./utils/uiLabelsAugment";
import enTranslation from "../public/locales/en/translation.json";
import frTranslation from "../public/locales/fr/translation.json";
import nlTranslation from "../public/locales/nl/translation.json";
import esTranslation from "../public/locales/es/translation.json";
import deTranslation from "../public/locales/de/translation.json";
import jaTranslation from "../public/locales/ja/translation.json";
import idTranslation from "../public/locales/id/translation.json";

// Import additional namespaces
import enFastResearch from "../public/locales/en/fastresearch.json";
import jaFastResearch from "../public/locales/ja/fastresearch.json";
import frFastResearch from "../public/locales/fr/fastresearch.json";
import deFastResearch from "../public/locales/de/fastresearch.json";
import enQuickstart from "../public/locales/en/quickstart.json";
import jaQuickstart from "../public/locales/ja/quickstart.json";
import deQuickstart from "../public/locales/de/quickstart.json";
import idFastResearch from "../public/locales/id/fastresearch.json";
import idQuickstart from "../public/locales/id/quickstart.json";

const preloadResources = {
  en: {
    translation: enTranslation,
    fastresearch: enFastResearch,
    quickstart: enQuickstart
  },
  fr: {
    translation: frTranslation,
    fastresearch: frFastResearch
  },
  nl: { translation: nlTranslation },
  es: { translation: esTranslation },
  de: {
    translation: deTranslation,
    fastresearch: deFastResearch,
    quickstart: deQuickstart
  },
  ja: {
    translation: jaTranslation,
    fastresearch: jaFastResearch,
    quickstart: jaQuickstart
  },
  id: {
    translation: idTranslation,
    fastresearch: idFastResearch,
    quickstart: idQuickstart
  },
};

// Fallback resources in case HTTP backend fails
const fallbackResources = preloadResources;

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: fallbackResources,
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      // Add debug logging
      crossDomain: false
    },
    ns: ["translation", "fastresearch", "quickstart"],
    defaultNS: "translation",
    fallbackLng: "en",
    supportedLngs: ALL_CODES,
    interpolation: { escapeValue: false },
    detection: {
      order: ["querystring", "localStorage", "navigator"],
      caches: ["localStorage"], // Cache detected language so it persists
      lookupQuerystring: "lng", // ?lng=de, ?lng=ja, etc.
      lookupLocalStorage: "i18nextLng", // Use default i18next key
    },
    debug: true, // Enable debug always for now
  })
  .then(() => {
    console.log('i18next initialized successfully');
    console.log('Current language:', i18n.language);
    console.log('Available languages:', i18n.languages);

    // Sync AWS Amplify I18n with i18next language
    const syncAmplifyLanguage = () => {
      const currentLang = i18n.language;
      if (currentLang === 'ja') {
        I18n.setLanguage('ja');
      } else {
        I18n.setLanguage('en');
      }
    };

    // Merge the v2 persona UI label bundle (keys missing from the locale
    // JSON files + enum display vocabularies) — deep merge, no overwrite
    Object.entries(personaV2Labels).forEach(([lng, resources]) => {
      i18n.addResourceBundle(lng, "translation", resources, true, false);
    });
    Object.entries(uiLabelsAugment).forEach(([lng, resources]) => {
      i18n.addResourceBundle(lng, "translation", resources, true, false);
    });

    // Set initial Amplify language
    syncAmplifyLanguage();

    // Listen for language changes
    i18n.on('languageChanged', syncAmplifyLanguage);
  })
  .catch((err) => {
    console.error('i18next initialization failed:', err);
  });

export default i18n; 